/**
 * Админ-список клиентов (E11, ФТ-6.1–6.3).
 */

import { z } from "zod";
import { accessTypeSchema } from "./access-grants";
import { decimalStringSchema } from "./catalog";
import { apiSuccessSchema } from "./envelope";
import { projectStatusSchema, projectStepTypeSchema } from "./projects";

export const clientStatusSchema = z.enum(["active", "annulled", "pending_deletion", "deleted"]);
export type ClientStatus = z.infer<typeof clientStatusSchema>;

/** Индикатор строки по активным допускам (ФТ-6.1 / доступность). */
export const clientAccessToneSchema = z.enum([
  "price",
  "bom_reference",
  "registered",
]);
export type ClientAccessTone = z.infer<typeof clientAccessToneSchema>;

export const adminClientListItemSchema = z.object({
  id: z.string().uuid(),
  personalCode: z.string().length(8),
  registeredAt: z.string().datetime(),
  /** Дата регистрации либо последнего обновления проекта клиента — что позже. */
  lastActivityAt: z.string().datetime(),
  company: z.string().min(1),
  position: z.string().min(1),
  fullName: z.string().min(1),
  phone: z.string().min(1),
  email: z.string().email(),
  region: z.string().nullable(),
  deliveryObject: z.string().nullable(),
  status: clientStatusSchema,
  assignedStaffId: z.string().uuid().nullable(),
  assignedStaffName: z.string().nullable(),
  activeAccessTypes: z.array(accessTypeSchema),
  accessTone: clientAccessToneSchema,
  /** Персональная надбавка, % (ФТ-10.5); не отдаётся клиентскому SPA. */
  priceMarkup: decimalStringSchema,
  deleteScheduledAt: z.string().datetime().nullable(),
});
export type AdminClientListItem = z.infer<typeof adminClientListItemSchema>;

export const adminClientListFiltersSchema = z.object({
  personalCode: z.string().trim().max(8).optional(),
  company: z.string().trim().max(255).optional(),
  position: z.string().trim().max(255).optional(),
  fullName: z.string().trim().max(255).optional(),
  phone: z.string().trim().max(64).optional(),
  email: z.string().trim().max(255).optional(),
  region: z.string().trim().max(255).optional(),
  deliveryObject: z.string().trim().max(512).optional(),
  assignedStaffName: z.string().trim().max(255).optional(),
  status: clientStatusSchema.optional(),
});
export type AdminClientListFilters = z.infer<typeof adminClientListFiltersSchema>;

export const adminClientListResponseSchema = apiSuccessSchema(z.array(adminClientListItemSchema));

/** Установка уровня допуска клиента напрямую (E11/E09, «Изменить уровень допуска»). */
export const setClientAccessToneRequestSchema = z.object({
  tone: clientAccessToneSchema,
});
export type SetClientAccessToneRequest = z.infer<typeof setClientAccessToneRequestSchema>;

export const setClientAccessToneResponseSchema = apiSuccessSchema(
  z.object({ tone: clientAccessToneSchema }),
);

export const CLIENT_ADMIN_ERROR_CODES = {
  NOT_FOUND: "CLIENT_ADMIN_NOT_FOUND",
  INVALID_STATUS: "CLIENT_ADMIN_INVALID_STATUS",
  STAFF_NOT_FOUND: "CLIENT_ADMIN_STAFF_NOT_FOUND",
} as const;
export type ClientAdminErrorCode =
  (typeof CLIENT_ADMIN_ERROR_CODES)[keyof typeof CLIENT_ADMIN_ERROR_CODES];

/** Ответ после планирования/отмены удаления / аннулирования. */
export const adminClientMutationResponseSchema = apiSuccessSchema(adminClientListItemSchema);

export const assignClientStaffRequestSchema = z.object({
  assignedStaffId: z.string().uuid().nullable(),
});
export type AssignClientStaffRequest = z.infer<typeof assignClientStaffRequestSchema>;

export const staffOptionSchema = z.object({
  id: z.string().uuid(),
  fullName: z.string().min(1),
  email: z.string().email(),
});
export type StaffOption = z.infer<typeof staffOptionSchema>;

export const staffOptionsResponseSchema = apiSuccessSchema(z.array(staffOptionSchema));

/** Результат фоновой/ручной финализации отсроченных удалений. */
export const finalizeDueDeletionsResultSchema = z.object({
  finalizedCount: z.number().int().nonnegative(),
  clientIds: z.array(z.string().uuid()),
});
export type FinalizeDueDeletionsResult = z.infer<typeof finalizeDueDeletionsResultSchema>;

export const finalizeDueDeletionsResponseSchema = apiSuccessSchema(finalizeDueDeletionsResultSchema);

/** Массовое планирование удаления (ФТ-6.5 / bulk). */
export const bulkScheduleDeletionRequestSchema = z.object({
  clientIds: z.array(z.string().uuid()).min(1).max(500),
});
export type BulkScheduleDeletionRequest = z.infer<typeof bulkScheduleDeletionRequestSchema>;

export const bulkScheduleDeletionResultSchema = z.object({
  scheduledCount: z.number().int().nonnegative(),
  skippedCount: z.number().int().nonnegative(),
  clients: z.array(adminClientListItemSchema),
});
export type BulkScheduleDeletionResult = z.infer<typeof bulkScheduleDeletionResultSchema>;

export const bulkScheduleDeletionResponseSchema = apiSuccessSchema(bulkScheduleDeletionResultSchema);

/** Расчёт в карточке клиента (админка). */
export const adminClientCalculationSchema = z.object({
  id: z.string().uuid(),
  modelName: z.string().min(1),
  unitCode: z.string().nullable(),
  selectedSize: z.string().nullable(),
  preliminaryPrice: decimalStringSchema.nullable(),
  createdAt: z.string().datetime(),
});
export type AdminClientCalculation = z.infer<typeof adminClientCalculationSchema>;

/** Проект клиента с расчётами. */
export const adminClientProjectSchema = z.object({
  id: z.string().uuid(),
  status: projectStatusSchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  currentSteps: z.array(projectStepTypeSchema),
  calculations: z.array(adminClientCalculationSchema),
});
export type AdminClientProject = z.infer<typeof adminClientProjectSchema>;

export const adminClientGrantSchema = z.object({
  accessType: accessTypeSchema,
  status: z.enum(["pending", "active", "revoked"]),
});
export type AdminClientGrant = z.infer<typeof adminClientGrantSchema>;

/** Карточка клиента для админки. */
export const adminClientDetailSchema = z.object({
  client: adminClientListItemSchema,
  grants: z.array(adminClientGrantSchema),
  projects: z.array(adminClientProjectSchema),
});
export type AdminClientDetail = z.infer<typeof adminClientDetailSchema>;

export const adminClientDetailResponseSchema = apiSuccessSchema(adminClientDetailSchema);
